import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, type ReactNode, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import '@fontsource-variable/albert-sans';
import '@fontsource/libre-baskerville/400-italic.css';
import { App } from './App';
import { mountLogoSprite } from './logoSprite';
import './styles/tokens.css';
import './nivio-ui.css';
import { UpdatePrompt } from './components/UpdatePrompt';
import { ProductGuard } from './productProfile';
import { captureInstallPrompt, registerServiceWorker } from './pwa';
import { captureShareParam } from './share';

const Admin = lazy(() => import('./pages/Admin').then(({ Admin }) => ({ default: Admin })));
const Artifacts = lazy(() =>
  import('./pages/Artifacts').then(({ Artifacts }) => ({ default: Artifacts })),
);
const Automations = lazy(() =>
  import('./pages/Automations').then(({ Automations }) => ({ default: Automations })),
);
const Conversation = lazy(() =>
  import('./pages/Conversation').then(({ Conversation }) => ({ default: Conversation })),
);
const Approval = lazy(() =>
  import('./pages/Approval').then(({ Approval }) => ({ default: Approval })),
);
const Duet = lazy(() => import('./pages/Duet').then(({ Duet }) => ({ default: Duet })));
const Home = lazy(() => import('./pages/Home').then(({ Home }) => ({ default: Home })));
const Login = lazy(() => import('./pages/Login').then(({ Login }) => ({ default: Login })));
const Landscape = lazy(() =>
  import('./pages/Landscape').then(({ Landscape }) => ({ default: Landscape })),
);
const Conversations = lazy(() =>
  import('./pages/Conversations').then(({ Conversations }) => ({ default: Conversations })),
);
const AgentTeamCreate = lazy(() =>
  import('./pages/automations/teams').then(({ AgentTeamCreate }) => ({ default: AgentTeamCreate })),
);
const Projects = lazy(() =>
  import('./pages/Projects').then(({ Projects }) => ({ default: Projects })),
);
const Settings = lazy(() =>
  import('./pages/Settings').then(({ Settings }) => ({ default: Settings })),
);
const Skills = lazy(() => import('./pages/Skills').then(({ Skills }) => ({ default: Skills })));

function page(element: ReactNode) {
  return (
    <Suspense fallback={<div className="h-full bg-bg-window" aria-busy="true" />}>
      {element}
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

captureInstallPrompt();
captureShareParam();
void registerServiceWorker();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing in index.html');

mountLogoSprite();
createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UpdatePrompt />
        <Routes>
          <Route path="/login" element={page(<Login />)} />
          <Route path="/landscape" element={page(<Landscape />)} />
          <Route element={<App />}>
            <Route path="/" element={page(<Home />)} />
            <Route
              path="/admin"
              element={page(
                <ProductGuard capability="advancedAdmin">
                  <Admin />
                </ProductGuard>,
              )}
            />
            <Route path="/ai-apps" element={<Navigate to="/" replace />} />
            <Route path="/duet" element={<Navigate to="/nivi" replace />} />
            <Route path="/desk" element={<Navigate to="/nivi" replace />} />
            <Route
              path="/skills"
              element={page(
                <ProductGuard capability="skills">
                  <Skills />
                </ProductGuard>,
              )}
            />
            <Route
              path="/artifacts"
              element={page(
                <ProductGuard capability="artifacts">
                  <Artifacts />
                </ProductGuard>,
              )}
            />
            <Route path="/conversations" element={page(<Conversations />)} />
            <Route path="/projects" element={page(<Projects />)} />
            <Route path="/settings" element={page(<Settings />)} />
            <Route
              path="/automations"
              element={page(
                <ProductGuard capability="automations">
                  <Automations />
                </ProductGuard>,
              )}
            />
            <Route
              path="/agents/teams/new"
              element={page(
                <ProductGuard capability="agents">
                  <AgentTeamCreate />
                </ProductGuard>,
              )}
            />
            <Route
              path="/agents/teams/:teamId"
              element={page(
                <ProductGuard capability="agents">
                  <AgentTeamCreate />
                </ProductGuard>,
              )}
            />
            <Route
              path="/agents"
              element={page(
                <ProductGuard capability="agents">
                  <Automations agentMode />
                </ProductGuard>,
              )}
            />
            <Route
              path="/nivi"
              element={page(
                <ProductGuard capability="agents">
                  <Duet />
                </ProductGuard>,
              )}
            />
            <Route
              path="/approvals/:approvalId"
              element={page(
                <ProductGuard capability="agents">
                  <Approval />
                </ProductGuard>,
              )}
            />
            <Route path="/c/:conversationId" element={page(<Conversation />)} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
